import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { Checkbox, CloseIcon, Select, Tooltip } from "@signet/ui";
import { api } from "./api.ts";
import { VERBS, type Matrix, type ResourceRow, type ScopedMatrix, type Verb } from "./me.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The permission matrix: modules down the side, four verbs across.

   Three states per cell. Ticked here (a direct grant), ticked by a role
   (shown ticked and dimmed, and not editable here, because it is changed on
   the role), and not held. A cell the person editing could not grant
   themselves is disabled, so the screen never offers what the server would
   drop. Ticking create, edit or delete ticks view; unticking view clears the
   row, the same rule the server applies.
   ────────────────────────────────────────────────────────────────────────── */

const LABEL: Record<Verb, string> = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

export function toggleVerb(matrix: Matrix, resource: string, verb: Verb, on: boolean): Matrix {
  const current = new Set(matrix[resource] ?? []);
  if (on) {
    current.add(verb);
    current.add("view");
  } else if (verb === "view") {
    current.clear();
  } else {
    current.delete(verb);
  }
  const next = { ...matrix };
  const verbs = VERBS.filter((v) => current.has(v));
  if (verbs.length) next[resource] = verbs;
  else delete next[resource];
  return next;
}

export function toggleScopedVerb(scoped: ScopedMatrix, resource: string, scope: string, verb: Verb, on: boolean): ScopedMatrix {
  const byScope = { ...(scoped[resource] ?? {}) };
  const current = new Set(byScope[scope] ?? []);
  if (on) {
    current.add(verb);
    current.add("view");
  } else if (verb === "view") current.clear();
  else current.delete(verb);
  const verbs = VERBS.filter((v) => current.has(v));
  if (verbs.length) byScope[scope] = verbs;
  else delete byScope[scope];
  const next = { ...scoped };
  if (Object.keys(byScope).length) next[resource] = byScope;
  else delete next[resource];
  return next;
}

export function MatrixEditor({
  resources,
  value,
  inherited = {},
  grantable,
  onChange,
  readOnly = false,
  scoped,
  inheritedScoped = {},
  grantableScoped = {},
  onScopedChange,
}: {
  resources: ResourceRow[];
  value: Matrix;
  inherited?: Matrix;
  /** "all" for the owner; otherwise the editor's own matrix. */
  grantable: Matrix | "all";
  onChange(next: Matrix): void;
  readOnly?: boolean;
  /** Per-record grants. Omit to hide the per-record controls entirely. */
  scoped?: ScopedMatrix;
  inheritedScoped?: ScopedMatrix;
  /** The editor's own per-record grants, for deciding what they may hand out. */
  grantableScoped?: ScopedMatrix;
  onScopedChange?(next: ScopedMatrix): void;
}) {
  const modules = [...new Map(resources.map((r) => [r.module, r.moduleName])).entries()];
  const canGrant = (resource: string, verb: Verb) => grantable === "all" || (grantable[resource]?.includes(verb) ?? false);
  const canGrantAt = (resource: string, scope: string, verb: Verb) => canGrant(resource, verb) || (grantableScoped[resource]?.[scope]?.includes(verb) ?? false);

  return (
    <div className="matrix" role="table" aria-label="Permissions">
      <div className="matrix-row matrix-head" role="row">
        <span role="columnheader">What</span>
        {VERBS.map((verb) => (
          <span key={verb} role="columnheader" className="matrix-verb">{LABEL[verb]}</span>
        ))}
      </div>
      {modules.map(([moduleId, moduleName]) => (
        <Fragment key={moduleId}>
          <div className="matrix-module" role="row">
            <strong role="rowheader">{moduleName}</strong>
          </div>
          {resources
            .filter((r) => r.module === moduleId)
            .map((resource) => (
              <div className="matrix-row" role="row" key={resource.key}>
                <span className="matrix-resource" role="rowheader">
                  {resource.label}
                  {resource.description ? <small>{resource.description}</small> : null}
                </span>
                {VERBS.map((verb) => {
                  if (!resource.verbs.includes(verb)) return <span key={verb} className="matrix-cell none" role="cell" aria-label="Not applicable" />;
                  const fromRole = inherited[resource.key]?.includes(verb) ?? false;
                  const direct = value[resource.key]?.includes(verb) ?? false;
                  const disabled = readOnly || fromRole || !canGrant(resource.key, verb);
                  const box = (
                    <span className={`matrix-cell${fromRole ? " inherited" : ""}`} role="cell">
                      <Checkbox
                        checked={direct || fromRole}
                        disabled={disabled}
                        label={`${LABEL[verb]} ${resource.label}`}
                        onChange={(on) => onChange(toggleVerb(value, resource.key, verb, on))}
                      />
                    </span>
                  );
                  return fromRole ? (
                    <Tooltip key={verb} content="From a role. Change it on the role.">{box}</Tooltip>
                  ) : (
                    <Fragment key={verb}>{box}</Fragment>
                  );
                })}
                {resource.scopeLabel && scoped && onScopedChange && !(value[resource.key]?.includes("view")) ? (
                  <ScopedRows
                    resource={resource}
                    value={scoped[resource.key] ?? {}}
                    inherited={inheritedScoped[resource.key] ?? {}}
                    readOnly={readOnly}
                    canGrantAt={(scope, verb) => canGrantAt(resource.key, scope, verb)}
                    onToggle={(scope, verb, on) => onScopedChange(toggleScopedVerb(scoped, resource.key, scope, verb, on))}
                  />
                ) : null}
              </div>
            ))}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * The per-record grants under one resource: "only for these niches".
 *
 * Shown only while the resource is not granted globally, because a global
 * grant already covers every record and a list underneath it would be noise.
 */
function ScopedRows({
  resource,
  value,
  inherited,
  readOnly,
  canGrantAt,
  onToggle,
}: {
  resource: ResourceRow;
  value: Record<string, Verb[]>;
  inherited: Record<string, Verb[]>;
  readOnly: boolean;
  canGrantAt(scope: string, verb: Verb): boolean;
  onToggle(scope: string, verb: Verb, on: boolean): void;
}) {
  const options = useQuery({
    queryKey: ["scopes", resource.key],
    queryFn: () => api<{ label: string; options: { id: string; label: string }[] }>(`/api/admin/scopes/${resource.key}`),
    staleTime: 60_000,
  });
  const labelOf = (id: string) => options.data?.options.find((o) => o.id === id)?.label ?? id;
  const chosen = [...new Set([...Object.keys(value), ...Object.keys(inherited)])].sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
  const available = (options.data?.options ?? []).filter((o) => !chosen.includes(o.id));
  const noun = (resource.scopeLabel ?? "record").toLowerCase();

  return (
    <div className="matrix-scoped" role="group" aria-label={`${resource.label} for specific ${noun}s`}>
      {chosen.map((scope) => (
        <div className="matrix-row matrix-scope-row" key={scope}>
          <span className="matrix-resource">
            <span className="matrix-scope-name">Only {labelOf(scope)}</span>
          </span>
          {VERBS.map((verb) => {
            if (!resource.verbs.includes(verb)) return <span key={verb} className="matrix-cell none" />;
            const fromRole = inherited[scope]?.includes(verb) ?? false;
            const direct = value[scope]?.includes(verb) ?? false;
            return (
              <span key={verb} className={`matrix-cell${fromRole ? " inherited" : ""}`}>
                <Checkbox
                  checked={direct || fromRole}
                  disabled={readOnly || fromRole || !canGrantAt(scope, verb)}
                  label={`${verb} ${resource.label} for ${labelOf(scope)}`}
                  onChange={(on) => onToggle(scope, verb, on)}
                />
              </span>
            );
          })}
          {!readOnly && value[scope] && !inherited[scope] ? (
            <button type="button" className="icon-button ghost small matrix-scope-remove" aria-label={`Remove ${labelOf(scope)}`} onClick={() => onToggle(scope, "view", false)}>
              <CloseIcon size={12} />
            </button>
          ) : null}
        </div>
      ))}
      {!readOnly && available.length ? (
        <div className="matrix-scope-add">
          <Select
            label={`Give access to one ${noun}`}
            value={null}
            placeholder={`Only for one ${noun}...`}
            compact
            options={available.filter((o) => canGrantAt(o.id, "view")).map((o) => ({ value: o.id, label: o.label }))}
            onChange={(id) => id && onToggle(id, "view", true)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** "research.niche: delete" style lines for a toast after a clamped save. */
export function describeDropped(dropped: Matrix, resources: ResourceRow[]): string {
  return Object.entries(dropped)
    .map(([key, verbs]) => `${resources.find((r) => r.key === key)?.label ?? key}: ${verbs.join(", ")}`)
    .join("; ");
}
